/* Minouko search: native dialog + Shopify-rendered suggestions, no dependency. */
(() => {
  'use strict';
  const dialog = document.getElementById('search-dialog');
  if (!dialog || typeof dialog.showModal !== 'function') return;
  const input = dialog.querySelector('[name="q"]');
  const form = dialog.querySelector('form');
  const welcome = dialog.querySelector('[data-search-welcome]');
  const results = dialog.querySelector('[data-search-results]');
  const all = dialog.querySelector('[data-search-all]');
  const clear = dialog.querySelector('[data-search-clear]');
  const status = dialog.querySelector('[role="status"]');
  const scroll = dialog.querySelector('.finder__scroll');
  const desktop = matchMedia('(min-width: 780px)');
  const cache = new Map();
  let timer, controller, deadline, revision = 0, opener, previousOverflow, composing = false;
  let selected = null;
  const fullURL = (term) => {
    const url = new URL(form.action);
    url.search = new URLSearchParams({ q: term, type: 'product', 'options[prefix]': 'last' });
    return url.href;
  };
  function cancel() {
    ++revision;
    clearTimeout(timer);
    clearTimeout(deadline);
    controller?.abort();
    controller = null;
    results.removeAttribute('aria-busy');
  }
  function preview(item) {
    if (!item?.dataset.preview) return;
    selected = item;
    results.querySelectorAll('[data-preview]').forEach(row => row.classList.toggle('is-selected', row === item));
    const aside = results.querySelector('[data-search-preview]');
    const template = document.getElementById(item.dataset.preview);
    if (desktop.matches && aside && template && aside.dataset.current !== item.dataset.preview) {
      aside.replaceChildren(template.content.cloneNode(true));
      aside.dataset.current = item.dataset.preview;
    }
  }
  function showHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const response = doc.querySelector('[data-search-response]');
    if (!response) throw new Error('Invalid search response');
    results.replaceChildren(response);
    welcome.hidden = true;
    results.hidden = false;
    results.removeAttribute('aria-busy');
    status.textContent = results.querySelector('[data-search-announcement]')?.textContent || 'Suggestions disponibles.';
    selected = null;
    preview(results.querySelector('[data-preview]'));
  }
  function failure() {
    results.replaceChildren();
    const message = document.createElement('p');
    message.className = 'finder__empty';
    message.textContent = 'Les suggestions sont momentanément indisponibles. Lancez la recherche complète pour continuer.';
    results.append(message);
    results.hidden = false;
    welcome.hidden = true;
    results.removeAttribute('aria-busy');
    status.textContent = 'Suggestions indisponibles. La recherche complète reste accessible.';
  }
  async function query(term, token) {
    controller = new AbortController();
    const request = controller;
    // A stalled request gets the same useful fallback as an HTTP/network error.
    deadline = setTimeout(() => request.abort(), 8000);
    try {
      const url = new URL(dialog.dataset.suggestUrl, location.origin);
      url.search = new URLSearchParams({
        q: term, section_id: 'predictive-search', 'resources[type]': 'product,collection',
        'resources[limit]': '4', 'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last'
      });
      const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Search HTTP ' + response.status);
      const html = await response.text();
      if (token !== revision || !dialog.open) return;
      showHTML(html);
      cache.set(term, html);
      if (cache.size > 20) cache.delete(cache.keys().next().value);
    } catch (_) {
      if (token === revision && dialog.open) failure();
    } finally {
      if (token === revision) { clearTimeout(deadline); controller = null; }
    }
  }
  function update() {
    cancel();
    const term = input.value.trim();
    clear.hidden = !input.value;
    all.href = fullURL(term);
    all.hidden = !term;
    selected = null;
    results.replaceChildren();
    scroll.scrollTop = 0;
    if (term.length < 2 || composing) {
      welcome.hidden = false;
      results.hidden = true;
      status.textContent = term ? 'Saisissez au moins deux caractères pour les suggestions.' : 'Recherchez un produit ou explorez ses habitudes.';
      return;
    }
    welcome.hidden = true;
    results.hidden = false;
    results.setAttribute('aria-busy', 'true');
    status.textContent = 'Recherche en cours…';
    if (cache.has(term)) { showHTML(cache.get(term)); return; }
    const token = revision;
    timer = setTimeout(() => query(term, token), 180);
  }
  function open(source) {
    if (dialog.open) { input.focus(); return; }
    opener = source || document.activeElement;
    previousOverflow = document.documentElement.style.overflow;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    dialog.querySelectorAll('[data-src]').forEach(img => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
    update();
    input.focus({ preventScroll: true });
    input.select();
    viewport();
  }
  function close() { if (dialog.open) dialog.close(); }
  dialog.addEventListener('close', () => {
    cancel();
    document.documentElement.style.overflow = previousOverflow || '';
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-search-open]');
    if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    open(trigger);
  });
  let backdropDown = false;
  dialog.addEventListener('pointerdown', event => { backdropDown = event.target === dialog; });
  dialog.addEventListener('click', event => {
    if (event.target.closest('[data-search-close]') || (event.target === dialog && backdropDown)) close();
    if (event.target.closest('[data-search-clear]')) { input.value = ''; update(); input.focus(); }
  });
  input.addEventListener('compositionstart', () => { composing = true; cancel(); });
  input.addEventListener('compositionend', () => { composing = false; update(); });
  input.addEventListener('input', update);
  form.addEventListener('submit', event => {
    if (composing || !input.value.trim()) { event.preventDefault(); input.focus(); }
    else { input.value = input.value.trim(); cancel(); }
  });
  results.addEventListener('pointerover', event => preview(event.target.closest('[data-preview]')));
  results.addEventListener('focusin', event => preview(event.target.closest('[data-preview]')));
  desktop.addEventListener('change', () => { if (selected) preview(selected); });
  dialog.addEventListener('keydown', event => {
    if (event.isComposing || composing || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'Tab') {
      const nodes = [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([type=hidden]):not([disabled])')].filter(el => el.getClientRects().length);
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      return;
    }
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (document.activeElement !== input && !document.activeElement.matches('[data-search-item]')) return;
    const links = [...results.querySelectorAll('[data-search-item]')];
    if (!links.length) return;
    event.preventDefault();
    const at = links.indexOf(document.activeElement);
    const next = at < 0 ? (event.key === 'ArrowDown' ? 0 : links.length - 1) : at + (event.key === 'ArrowDown' ? 1 : -1);
    if (next < 0 || next >= links.length) input.focus();
    else { links[next].focus({ preventScroll: true }); links[next].scrollIntoView({ block: 'nearest' }); }
  });
  document.addEventListener('keydown', event => {
    const active = document.activeElement;
    if (event.defaultPrevented || event.isComposing || event.repeat || active?.closest('input,textarea,select,[contenteditable]:not([contenteditable="false"]),[role="textbox"]')) return;
    if (document.querySelector('dialog[open], .drawer[open]')) return;
    if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey) { event.preventDefault(); open(); }
  });
  function viewport() {
    if (!dialog.open || !window.visualViewport) return;
    dialog.style.setProperty('--finder-vh', window.visualViewport.height + 'px');
    dialog.style.setProperty('--finder-top', window.visualViewport.offsetTop + 'px');
  }
  window.visualViewport?.addEventListener('resize', viewport);
  window.visualViewport?.addEventListener('scroll', viewport);
  window.addEventListener('pagehide', close);
})();
